import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Spinner } from 'react-bootstrap';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, ShieldCheck } from 'lucide-react';

import {
    getConsistencyFindings,
    dismissConsistencyFinding,
    consistencyLabel,
    severityBadge,
    consistencyDetailSummary,
} from '../../../services/InsightsService';

/**
 * Kvalitetskontroll — the consistency-check findings list (design §4.8, Phase 4
 * item 2). A section on Insikter, gated by the 'auto_post' feature by the
 * parent (and again by the endpoint, which 403s when absent).
 *
 * Rule-based QC over 100% of bookings surfaces OPEN findings; each shows a
 * Swedish finding-type label, a severity badge, a one-line detail summary, and
 * an "Avfärda" action. Empty (or absent) → "Inga avvikelser".
 *
 * Graceful by contract: a 403/404 hides the section; any other error shows an
 * inline off-state, never a crash.
 */

export default function ConsistencyFindingsSection({ companyId }) {
    const [loading, setLoading] = useState(true);
    const [hidden, setHidden] = useState(false);
    const [error, setError] = useState(null);
    const [findings, setFindings] = useState([]);
    const [dismissing, setDismissing] = useState(null);

    useEffect(() => {
        if (!companyId) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        getConsistencyFindings(companyId)
            .then((data) => {
                if (cancelled) return;
                setFindings(Array.isArray(data?.findings) ? data.findings : []);
                setHidden(false);
            })
            .catch((err) => {
                if (cancelled) return;
                const status = err?.response?.status;
                if (status === 403 || status === 404) {
                    setHidden(true);
                } else {
                    setError(err?.response?.data?.message || err.message || 'Kunde inte hämta avvikelser');
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [companyId]);

    const handleDismiss = useCallback(async (finding) => {
        setDismissing(finding.id);
        try {
            await dismissConsistencyFinding(companyId, finding.id);
            setFindings((prev) => prev.filter((f) => f.id !== finding.id));
            toast.success('Avvikelsen avfärdades.');
        } catch (err) {
            const status = err?.response?.status;
            toast.error(status === 404
                ? 'Avvikelsen finns inte längre — uppdatera sidan.'
                : (err?.response?.data?.message || err.message || 'Kunde inte avfärda avvikelsen'));
        } finally {
            setDismissing(null);
        }
    }, [companyId]);

    if (hidden) return null;

    return (
        <div className="insights-box">
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#1f2937', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ShieldCheck size={16} style={{ color: '#6b7280' }} />
                    Kvalitetskontroll
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
                    Regelbaserad kontroll av bokförda verifikat — misstänkta avvikelser att titta på.
                </div>
            </div>

            {loading ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Laddar…
                </div>
            ) : error ? (
                <div style={{ padding: 16 }}>
                    <Alert variant="warning" className="mb-0 py-2" style={{ fontSize: '0.8125rem' }}>
                        <AlertCircle size={14} className="me-2" />
                        {error}
                    </Alert>
                </div>
            ) : findings.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: '0.8125rem' }}>
                    <CheckCircle2 size={18} style={{ color: '#16a34a', marginBottom: 6 }} />
                    <div>Inga avvikelser</div>
                </div>
            ) : (
                <div>
                    {findings.map((finding) => {
                        const badge = severityBadge(finding.severity);
                        return (
                            <div
                                key={finding.id}
                                style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 12,
                                    padding: '12px 16px', borderBottom: '1px solid #f3f4f6',
                                }}
                            >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#1f2937' }}>
                                            {consistencyLabel(finding.finding_type)}
                                        </span>
                                        <Badge bg={badge.variant} style={{ fontSize: '0.6875rem' }}>
                                            {badge.label}
                                        </Badge>
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 3 }}>
                                        {consistencyDetailSummary(finding)}
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={dismissing === finding.id}
                                    onClick={() => handleDismiss(finding)}
                                    style={{ flexShrink: 0 }}
                                >
                                    {dismissing === finding.id ? <Spinner animation="border" size="sm" /> : 'Avfärda'}
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
